import { lastSetsWithExtra, type LastLoggedAnySet } from "./lastLoggedSets.js";
import type { LastLoggedSet } from "./resolveDefaultWeight.js";
import { TODAY_MAX_SETS, todayMaxSets, todaySetCap } from "./todayRegap.js";
import {
  altHasHistory,
  freeTodayPick,
  type TodayDraftRow,
  type TodayTarget,
} from "./todaySessionProposal.js";

// AP-9c: the edits a built «today» draft takes before Start. The time budget
// sets how many sets each row gets; the shuffle swaps a row's exercise for
// the next one that closes the same gap. Pure: the logger hands in the rest
// of each exercise and writes the result back through its own hydration.

/** The time chips of the draft card, in minutes. */
export const TODAY_MINUTE_CHOICES = [30, 45, 60, 90] as const;

/** The budget before the athlete picks one. */
export const TODAY_DEFAULT_MINUTES = 60;

/** D5, D7: the intensity chips, in percent of the weekly target (MEV, MAV).
 *  They scale the volume only: the load and the RIR stay. */
export const TODAY_INTENSITY_CHOICES = [50, 70, 85, 100] as const;

/** The intensity before the athlete picks one. */
export const TODAY_DEFAULT_INTENSITY = 100;

/** Seconds one working set takes to perform, rest excluded: a round figure
 *  for 8 to 12 reps with the setup. The estimate adds the rest on top. */
export const TODAY_SET_WORK_SEC = 45;

type BudgetRow = {
  gap: number;
  /** Sets that would close the gap with this exercise alone. */
  setsToClose: number;
  /** The exercise's rest between sets, in seconds. */
  restSec: number;
  /** Absent: "mev". */
  target?: TodayTarget;
};

const setSec = (restSec: number): number => Math.max(0, restSec) + TODAY_SET_WORK_SEC;

/** Sets per row that fit in `minutes`, index-aligned with `rows`. Three
 *  passes, biggest gap first in each (Antenore, 26/09): the MEV rows up to
 *  their clamp (todaySetCap), then the MEV rows up to `maxSets`, then the MAV
 *  rows up to their clamp. `maxSets` (todayMaxSets) caps all three, so time
 *  left over ends the draft early. A row with 0 sets is out of the draft, and
 *  so is a row with no gap left (D4: the rows above covered it). `inOrder`:
 *  the rows' own order instead of the biggest gap (a draft that ignored rest
 *  comes most rested first). */
export function fitTodayBudget(
  rows: ReadonlyArray<BudgetRow>,
  minutes: number,
  maxSets: number = TODAY_MAX_SETS,
  inOrder = false,
): number[] {
  const out = rows.map(() => 0);
  let left = Math.max(0, minutes) * 60;
  const order = rows
    .map((_, i) => i)
    .sort((a, b) => (inOrder ? 0 : (rows[b]?.gap ?? 0) - (rows[a]?.gap ?? 0)) || a - b);
  const mev = order.filter((i) => rows[i]?.target !== "mav");
  const mav = order.filter((i) => rows[i]?.target === "mav");
  const raise = (idx: number[], upTo: (row: BudgetRow) => number) => {
    for (const i of idx) {
      const row = rows[i];
      if (!row || row.gap <= 0) continue;
      const cost = setSec(row.restSec);
      const n = Math.max(0, Math.min(upTo(row) - (out[i] ?? 0), Math.floor(left / cost)));
      out[i] = (out[i] ?? 0) + n;
      left -= n * cost;
    }
  };
  raise(mev, (row) => todaySetCap(row.setsToClose, maxSets));
  raise(mev, () => maxSets);
  raise(mav, (row) => todaySetCap(row.setsToClose, maxSets));
  return out;
}

/** Whole minutes the sets take, rest included. */
export const estimateTodayMinutes = (
  rows: ReadonlyArray<{ sets: number; restSec: number }>,
): number =>
  Math.round(rows.reduce((sum, r) => sum + r.sets * setSec(r.restSec), 0) / 60);

const currentOf = (row: TodayDraftRow) =>
  row.alternatives?.find((a) => a.exerciseId === row.exerciseId);

/** The row's next alternative after the one it holds, wrapping to the first,
 *  skipping any exercise another row holds. Sets stay as they are. The same
 *  row back when nothing else qualifies. */
const advance = (
  rows: ReadonlyArray<TodayDraftRow>,
  idx: number,
): TodayDraftRow | undefined => {
  const row = rows[idx];
  if (!row) return undefined;
  const taken = new Set(rows.filter((_, i) => i !== idx).map((r) => r.exerciseId));
  const alts = row.alternatives ?? [];
  const from = alts.findIndex((a) => a.exerciseId === row.exerciseId);
  for (let step = 1; step < alts.length; step += 1) {
    const next = alts[(from + step + alts.length) % alts.length];
    if (!next || taken.has(next.exerciseId)) continue;
    return { ...row, exerciseId: next.exerciseId, hasHistory: altHasHistory(next) };
  }
  return row;
};

/** Shuffle one row. */
export const shuffleTodayRow = (
  rows: ReadonlyArray<TodayDraftRow>,
  idx: number,
): TodayDraftRow[] => {
  const next = advance(rows, idx);
  return rows.map((r, i) => (i === idx && next ? next : r));
};

/** Shuffle every row in the draft (sets > 0), top to bottom. */
export const shuffleTodayRows = (
  rows: ReadonlyArray<TodayDraftRow>,
): TodayDraftRow[] =>
  rows.reduce<TodayDraftRow[]>(
    (acc, r, i) => (r.sets > 0 ? shuffleTodayRow(acc, i) : acc),
    [...rows],
  );

/** Refit the rows to `minutes` at `intensityPct` (the ceiling per row).
 *  `restSecOf` answers per exercise. `inOrder`: as on fitTodayBudget. */
export const fitTodayRows = (
  rows: ReadonlyArray<TodayDraftRow>,
  minutes: number,
  restSecOf: (exerciseId: string) => number,
  intensityPct = TODAY_DEFAULT_INTENSITY,
  inOrder = false,
): TodayDraftRow[] => {
  const sets = fitTodayBudget(
    rows.map((r) => ({
      gap: r.gap,
      setsToClose: currentOf(r)?.setsToClose ?? r.sets,
      restSec: restSecOf(r.exerciseId),
      ...(r.target ? { target: r.target } : {}),
    })),
    minutes,
    todayMaxSets(intensityPct),
    inOrder,
  );
  return rows.map((r, i) => ({ ...r, sets: sets[i] ?? 0 }));
};

/** D8, «Surprise me»: each row picks again, top to bottom, among its
 *  alternatives no row above holds. `random` present: a random one. Absent:
 *  the first, the most practised. Sets stay as they are. */
export const repickTodayRows = (
  rows: ReadonlyArray<TodayDraftRow>,
  random?: () => number,
): TodayDraftRow[] => {
  const taken = new Set<string>();
  return rows.map((r) => {
    const pick = freeTodayPick(r.alternatives ?? [], taken, random);
    if (!pick) {
      taken.add(r.exerciseId);
      return r;
    }
    taken.add(pick.exerciseId);
    return { ...r, exerciseId: pick.exerciseId, hasHistory: altHasHistory(pick) };
  });
};

/** The sets the weight chain reads for the exercises the rows hold: the
 *  anchor set (D1), kg or the extra on a bodyweight row. */
export const todayChainSets = (
  rows: ReadonlyArray<TodayDraftRow>,
): Map<string, LastLoggedSet> => {
  const legacy = new Map<string, LastLoggedAnySet>();
  const anchored = new Map<string, LastLoggedSet>();
  for (const r of rows) {
    const alt = currentOf(r);
    if (alt?.anchor?.chainSet) anchored.set(r.exerciseId, alt.anchor.chainSet);
    else if (alt?.anchor === undefined && alt?.last) legacy.set(r.exerciseId, alt.last);
  }
  return new Map([...lastSetsWithExtra(legacy), ...anchored]);
};
